import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, ArrowRight, CheckCircle2, KeyRound } from 'lucide-react';
import { api } from '../api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = email, 1 = code+password, 2 = done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(null);

  const emailValid = EMAIL_RE.test(email.trim());
  const codeValid = code.trim().length >= 4;
  const passValid = password.length >= 8;

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!emailValid) return;
    setLoading(true);
    setError('');
    try {
      await api.requestPasswordReset(email.trim());
      setStep(1);
    } catch (err) {
      setError(err.message || 'Could not send reset email. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (!codeValid || !passValid) return;
    setLoading(true);
    setError('');
    try {
      await api.confirmPasswordReset(code.trim(), password);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Invalid code or password too weak.');
    } finally {
      setLoading(false);
    }
  };

  const borderColor = (field) => {
    if (focused === field) return '#0EA5E9';
    const valid = field === 'email' ? emailValid && email.length > 0
      : field === 'code' ? codeValid
      : passValid;
    if (valid) return 'rgba(14,165,233,0.6)';
    return 'rgba(56,189,248,0.2)';
  };

  return (
    <div className="auth-root">
      <div className="auth-blobs">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
        <div className="auth-blob auth-blob-3" />
      </div>

      <div className="auth-card-wrap">
        <Link to="/login" className="auth-back-link">
          <ArrowLeft size={16} />
          <span>Back to sign in</span>
        </Link>

        <div className="auth-header">
          <Link to="/" className="auth-logo-link">
            <img src="/rivalogo.png" alt="RivalCam" className="auth-logo" />
          </Link>
          <h1 className="auth-title">
            {step === 2 ? 'Password reset!' : 'Forgot password?'}
          </h1>
          <p className="auth-subtitle">
            {step === 0 && "We'll send a reset code to your email."}
            {step === 1 && `Code sent to ${email}. Enter it below.`}
            {step === 2 && 'Your password has been updated.'}
          </p>
        </div>

        <div className="auth-card">
          <div className="auth-card-stripe" />

          {step === 0 && (
            <form onSubmit={handleRequestReset} className="auth-form" noValidate>
              <div className="auth-field">
                <label className="auth-label">Email</label>
                <div className="auth-input-wrap" style={{ borderColor: borderColor('email') }}>
                  <Mail size={18} className="auth-input-icon" color={focused === 'email' ? '#38BDF8' : '#64748B'} />
                  <input
                    type="email"
                    className="auth-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocused('email')}
                    onBlur={() => setFocused(null)}
                    autoComplete="email"
                  />
                  {emailValid && <CheckCircle2 size={18} color="#0EA5E9" className="auth-input-trail" />}
                </div>
              </div>

              {error && <div className="auth-server-error">{error}</div>}

              <button type="submit" className="auth-btn" disabled={!emailValid || loading}>
                {loading ? <span className="auth-spinner" /> : <><span>Send reset code</span><ArrowRight size={18} /></>}
              </button>
            </form>
          )}

          {step === 1 && (
            <form onSubmit={handleConfirmReset} className="auth-form" noValidate>
              <div className="auth-field">
                <label className="auth-label">Reset code</label>
                <div className="auth-input-wrap" style={{ borderColor: borderColor('code') }}>
                  <KeyRound size={18} className="auth-input-icon" color={focused === 'code' ? '#38BDF8' : '#64748B'} />
                  <input
                    type="text"
                    className="auth-input"
                    placeholder="Enter code from email"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onFocus={() => setFocused('code')}
                    onBlur={() => setFocused(null)}
                    autoComplete="one-time-code"
                  />
                  {codeValid && <CheckCircle2 size={18} color="#0EA5E9" className="auth-input-trail" />}
                </div>
              </div>

              <div className="auth-field">
                <label className="auth-label">New password</label>
                <div className="auth-input-wrap" style={{ borderColor: borderColor('password') }}>
                  <Lock size={18} className="auth-input-icon" color={focused === 'password' ? '#38BDF8' : '#64748B'} />
                  <input
                    type={showPass ? 'text' : 'password'}
                    className="auth-input"
                    placeholder="Min 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocused('password')}
                    onBlur={() => setFocused(null)}
                    autoComplete="new-password"
                  />
                  <button type="button" className="auth-eye" onClick={() => setShowPass(s => !s)}>
                    {showPass ? <EyeOff size={18} color="#64748B" /> : <Eye size={18} color="#64748B" />}
                  </button>
                </div>
              </div>

              {error && <div className="auth-server-error">{error}</div>}

              <button type="submit" className="auth-btn" disabled={!codeValid || !passValid || loading}>
                {loading ? <span className="auth-spinner" /> : <><span>Reset password</span><ArrowRight size={18} /></>}
              </button>

              <button type="button" className="auth-btn" disabled={loading}
                style={{ background: 'transparent', border: '1px solid rgba(56,189,248,0.2)', color: '#64748B', marginTop: 8 }}
                onClick={() => { setStep(0); setError(''); setCode(''); setPassword(''); }}>
                Resend code
              </button>
            </form>
          )}

          {step === 2 && (
            <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
              <CheckCircle2 size={48} color="#22C55E" style={{ marginBottom: 16 }} />
              <button className="auth-btn" onClick={() => navigate('/login')}>
                Sign in now <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>

        <p className="auth-footer-text">
          Remembered it?{' '}
          <Link to="/login" className="auth-footer-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
